import "./load-actions-cell.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Popup } from "@tql/ui-components-react/kendo/popup";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	arrowRight,
	dotsVerticalLine,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useRef, useState } from "react";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import type { Load } from "~/shared/schemas/loads-schema.ts";

const menuItems = ["Subscribe to Notifications"] as const;

type LoadActionsCellProps = GridCustomCellProps & {
	readonly onSubscribeToNotifications: (load: Load) => void;
};

export function LoadActionsCell(props: Readonly<LoadActionsCellProps>) {
	const { isEnabled: notificationsEnabled } = useFeatureFlagGate(
		"traxLtlNotifications",
	);
	const anchorRef = useRef<HTMLDivElement>(null);
	const [show, setShow] = useState(false);

	const handleOpenPopup = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			setShow(true);
		},
		[],
	);

	const handleClosePopup = useCallback(() => {
		setShow(false);
	}, []);

	const handleMenuItemClick = useCallback(
		(item: (typeof menuItems)[number]) => {
			const load = props.dataItem as Load;
			if (item === "Subscribe to Notifications") {
				props.onSubscribeToNotifications(load);
			}
			handleClosePopup();
		},
		[handleClosePopup, props],
	);

	if (!props.dataItem) {
		return <GridTd tdProps={props.tdProps}></GridTd>;
	}

	const load = props.dataItem as Load;
	const notificationsMenuItemEnabled =
		notificationsEnabled && load.rateType === "LTL";
	const anyMenuItemEnabled = notificationsMenuItemEnabled;

	return (
		<GridTd tdProps={props.tdProps}>
			{anyMenuItemEnabled ? (
				<>
					<div ref={anchorRef} className={"loads-action-trigger"}>
						<Button
							disabled={show}
							onClick={handleOpenPopup}
							startIcon={<SvgIcon icon={dotsVerticalLine} />}
							variant={"tertiary"}
						/>
					</div>
					<Popup
						anchor={anchorRef.current}
						anchorAlign={{ horizontal: "right", vertical: "bottom" }}
						animate={false}
						className={"loads-action-menu"}
						onMouseDownOutside={handleClosePopup}
						popupAlign={{ horizontal: "right", vertical: "top" }}
						show={show}
					>
						<div className={"loads-action-menu-items"}>
							{menuItems
								.filter(
									(item) =>
										item !== "Subscribe to Notifications" ||
										notificationsMenuItemEnabled,
								)
								.map((item) => (
									<div key={item} className={"loads-action-item"}>
										<Button
											onClick={() => handleMenuItemClick(item)}
											startIcon={<SvgIcon icon={arrowRight} />}
											variant={"tertiary"}
										>
											{item}
										</Button>
									</div>
								))}
						</div>
					</Popup>
				</>
			) : undefined}
		</GridTd>
	);
}
